import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Skeleton } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { PrimaryText } from '../CustomList/CustomList.styles';
import { StyledTableRow, StyledTableCell, CellContent } from './TipsTable/TipsTable.styles';
import { StyledAvatar } from '../StyledAvatar/StyledAvatar';
import { currencyFormatter } from '../../utils/helpers/formatting/currencyHelpers';
import { updateEmployee } from '../../redux/slices/employeesSlice';
import { selectCurrentUserId } from '../../redux/slices/currentUserSlice';
import { DRAWER_TYPES } from '../../pages/People/peopleDrawerConfig';
import { handleClickableKeyDown } from '../../utils/helpers/accessibility/keyboardHelpers';
import dayjs from '../../utils/dayjsConfig';
import { AlertClockIcon, CheckInCircleIcon } from '../../assets/svg';

const TipsListEntry = ({
  employee,
  handleOpenDrawer,
  lastPaymentDate,
  paidByName,
  paymentMethod,
  isLoadingPaymentData,
}) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectCurrentUserId);
  const nameParts = typeof paidByName === 'string' ? paidByName.trim().split(/\s+/).filter(Boolean) : [];
  const paidByFirstName = nameParts[0] || undefined;
  const paidByLastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : undefined;
  const formattedPaymentMethod = paymentMethod?.charAt(0).toUpperCase() + paymentMethod?.slice(1);
  const handlePaymentConfirm = async (paymentAmount, paymentMethod, paymentDate) => {
    if (!employee?.id) {
      throw new Error('Employee ID is required');
    }

    if (!currentUserId) {
      throw new Error('Current user ID is required');
    }

    // Convert payment amount from dollars to cents
    const payoutInCents = Math.round(paymentAmount * 100);

    try {
      const processTipPayoutCallable = httpsCallable(functions, 'processTipPayout');
      const result = await processTipPayoutCallable({
        employeeDocId: employee.id,
        payoutInCents,
        paymentMethod,
        paymentDate,
        paidByEid: String(currentUserId),
      });

      if (result.data?.success) {
        // Update Redux store with new tipsOwed value
        dispatch(
          updateEmployee({
            id: employee.id,
            updatedEmployee: {
              tipsOwed: result.data.newTipsOwed,
            },
          })
        );
      } else {
        throw new Error('Failed to process payment. Please try again.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);

      // Provide user-friendly error messages based on Firebase function error codes
      if (error.code === 'functions/failed-precondition') {
        throw new Error('Authentication required. Please make sure you are logged in.');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error('Invalid payment amount. Please check the amount and try again.');
      } else if (error.code === 'functions/not-found') {
        throw new Error('Employee not found. Please refresh the page and try again.');
      } else {
        throw new Error(error.message || 'Failed to process payment. Please try again.');
      }
    }
  };

  const handleRecordPayment = (e) => {
    e.stopPropagation(); // Prevent row click from firing
    handleOpenDrawer(
      DRAWER_TYPES.RECORD_PAYMENT,
      {
        employee,
        onConfirm: handlePaymentConfirm,
      },
      'Record a Payment'
    );
  };

  const handleRowClick = () => {
    handleOpenDrawer(DRAWER_TYPES.TIPS_HISTORY, employee, 'Tips History');
  };

  return (
    <StyledTableRow
      onClick={handleRowClick}
      onKeyDown={handleClickableKeyDown(handleRowClick)}
      tabIndex={0}
      aria-label={employee.displayName ? `View tips history for ${employee.displayName}` : 'View tips history'}
    >
      {/* Employee Info */}
      <StyledTableCell>
        <CellContent>
          <StyledAvatar size="small" alt={employee.displayName || 'User'} src={employee.profileImageUrl} />
          <PrimaryText variant="bodyRegular">{employee.displayName}</PrimaryText>
        </CellContent>
      </StyledTableCell>

      {/* Tips Owed */}
      <StyledTableCell>
        <PrimaryText variant="bodyRegular">
          {typeof employee.tipsOwed === 'number' ? currencyFormatter(employee.tipsOwed) : '$0.00'}
        </PrimaryText>
      </StyledTableCell>

      {/* Status */}
      <StyledTableCell>
        <CellContent>
          {employee.tipsOwed && employee.tipsOwed > 0 ? (
            <>
              <AlertClockIcon size={15} />
              <PrimaryText variant="bodyRegular">Payment Due</PrimaryText>
            </>
          ) : (
            <>
              <CheckInCircleIcon size={15} />
              <PrimaryText variant="bodyRegular">No Payment Due</PrimaryText>
            </>
          )}
        </CellContent>
      </StyledTableCell>

      {/* Last Payment Date */}
      <StyledTableCell>
        {isLoadingPaymentData ? (
          <Skeleton variant="text" width={60} height={20} />
        ) : (
          <PrimaryText variant="bodyRegular">
            {lastPaymentDate ? dayjs(lastPaymentDate).format('MMM D') : '—'}
          </PrimaryText>
        )}
      </StyledTableCell>

      {/* Paid By */}
      <StyledTableCell>
        {isLoadingPaymentData ? (
          <Skeleton variant="text" width={80} height={20} />
        ) : (
          <PrimaryText variant="bodyRegular">
            {paidByFirstName && paidByLastInitial ? `${paidByFirstName} ${paidByLastInitial}` : '—'}
          </PrimaryText>
        )}
      </StyledTableCell>

      {/* Payment Method */}
      <StyledTableCell>
        {isLoadingPaymentData ? (
          <Skeleton variant="text" width={70} height={20} />
        ) : (
          <PrimaryText variant="bodyRegular">{formattedPaymentMethod || '—'}</PrimaryText>
        )}
      </StyledTableCell>

      {/* Actions */}
      <StyledTableCell>
        <Button
          variant="primary"
          size="small"
          onClick={handleRecordPayment}
          disabled={!employee.tipsOwed || employee.tipsOwed <= 0}
          sx={{
            backgroundColor: (theme) => theme.colors.blue,
            color: (theme) => theme.colors.white,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.text.secondaryHover,
            },
            '&:disabled': {
              backgroundColor: (theme) => theme.palette.action.disabledBackground,
              color: (theme) => theme.palette.action.disabled,
            },
          }}
        >
          Record Payment
        </Button>
      </StyledTableCell>
    </StyledTableRow>
  );
};

TipsListEntry.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    profileImageUrl: PropTypes.string,
    tipsOwed: PropTypes.number,
  }).isRequired,
  handleOpenDrawer: PropTypes.func.isRequired,
  lastPaymentDate: PropTypes.number, // Unix timestamp in milliseconds
  paidByName: PropTypes.string,
  paymentMethod: PropTypes.string,
  isLoadingPaymentData: PropTypes.bool.isRequired,
};

export default TipsListEntry;
